import React from 'react';
import { toast } from 'sonner';
import { Loader2, Sparkles, Clock, Trash2, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useLanguage } from '../../contexts/LanguageContext';

export const IdentifiedStep = ({
  croppedPreview, plantData, editedName, setEditedName,
  onRecrop, onAccept, handleCompleteLater, saving,
  batchMode, onRemovePhoto, photoDupMatch,
  lookingUp, handleLookupByName
}) => {
  const { t } = useLanguage();
  return (
    <div className="space-y-5">
      {photoDupMatch && (
        <div
          className="flex items-start gap-2 rounded-lg px-3 py-2.5"
          style={{ backgroundColor: 'hsl(40, 90%, 95%)', border: '1px solid hsl(40, 70%, 80%)' }}
          data-testid="photo-duplicate-warning"
        >
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'hsl(35, 80%, 45%)' }} aria-hidden="true" />
          <p className="text-xs leading-relaxed" style={{ color: 'hsl(35, 45%, 28%)' }}>
            {photoDupMatch.where === 'library'
              ? (t('photoDupLibrary') || 'This exact photo is already in your library as "{name}". You can add it anyway or remove it.').replace('{name}', photoDupMatch.name || t('anotherPlant') || 'another plant')
              : (t('photoDupBatch') || 'You already added this photo earlier in this batch. You can add it anyway or remove it.')}
          </p>
        </div>
      )}
      {croppedPreview && (
        <div className="flex justify-center rounded-xl overflow-hidden" style={{ backgroundColor: 'hsl(82, 55%, 95%)' }}>
          <img src={croppedPreview} alt="Plant" className="max-w-full max-h-[200px] object-contain" />
        </div>
      )}
      <div className="rounded-xl p-5" style={{ backgroundColor: 'hsl(82, 55%, 95%)', border: '1px solid hsl(143, 28%, 85%)' }}>
        <p className="text-sm mb-1" style={{ color: 'hsl(125, 12%, 32%)' }}>{t('aiThinksThisIs')}</p>
        <p className="text-xl font-bold" style={{ color: 'hsl(143, 28%, 25%)' }} data-testid="identified-name">
          {plantData.common_name}
        </p>
        {plantData.scientific_name && (
          <p className="text-sm italic" style={{ color: 'hsl(125, 12%, 32%)' }}>{plantData.scientific_name}</p>
        )}
      </div>

      <div className="flex gap-2 flex-wrap">
        <Button
          onClick={onRecrop}
          variant="outline"
          style={{ borderColor: 'hsl(143, 28%, 85%)', color: 'hsl(143, 28%, 25%)' }}
          data-testid="try-again-button"
        >
          {t('recrop')}
        </Button>
        <Button
          onClick={onAccept}
          className="flex-1"
          style={{ backgroundColor: 'hsl(143, 28%, 25%)', color: 'white' }}
          data-testid="accept-identification-button"
        >
          {t('looksCorrect')}
        </Button>
        <Button
          onClick={handleCompleteLater}
          disabled={saving}
          variant="outline"
          style={{ borderColor: 'hsl(21, 70%, 75%)', color: 'hsl(21, 70%, 45%)' }}
          data-testid="complete-later-identified-button"
        >
          <Clock className="w-4 h-4 mr-1" />
          {saving ? t('saving') : t('completeLater')}
        </Button>
      </div>

      {batchMode && onRemovePhoto && (
        <div className="flex justify-center">
          <button
            onClick={onRemovePhoto}
            className="inline-flex items-center gap-1.5 text-sm font-medium underline"
            style={{ color: 'hsl(0, 60%, 45%)' }}
            data-testid="remove-photo-button"
          >
            <Trash2 className="w-3.5 h-3.5" />
            {t('removeThisPhoto') || 'Remove this photo'}
          </button>
        </div>
      )}

      <div className="border-t pt-4" style={{ borderColor: 'hsl(143, 28%, 85%)' }}>
        <p className="text-sm mb-2" style={{ color: 'hsl(125, 12%, 32%)' }}>{t('wrongEnterCorrectName')}</p>
        <div className="flex gap-2">
          <Input value={editedName} onChange={(e) => setEditedName(e.target.value)}
            placeholder="e.g., Fiddle Leaf Fig" data-testid="corrected-name-input" />
          <Button
            onClick={() => {
              if (!editedName.trim()) { toast.error(t('pleaseEnterName')); return; }
              handleLookupByName(editedName.trim());
            }}
            disabled={lookingUp || !editedName.trim()}
            style={{ backgroundColor: 'hsl(21, 70%, 57%)', color: 'white' }}
            data-testid="lookup-name-button"
          >
            {lookingUp ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
};
